/**
 * TransferCard — uTorrent-style single transfer row.
 * Uses useTheme(), no hardcoded colors, no direct useConfigStore.
 */
import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert } from 'react-native';
import { TransferSession } from '@share/domain';
import { useTheme } from '../hooks/useTheme';
import { Colors, Spacing, Radius, FontSize, FontWeight } from '../theme/tokens';
import { Icon } from '../atoms/Icon';
import { ProgressBar } from '../molecules/ProgressBar';

import { formatBytes as fmtBytes, formatSpeed as fmtSpeed, formatEta as fmtEta } from '../../../shared/utils/formatters';

// ─── Stat chip ────────────────────────────────────────────────────────────────

const Stat: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <View style={statStyles.statGroup}>
    <Text style={[statStyles.sep, { color }]}>|</Text>
    <Text style={[statStyles.text, { color }]}>{label}</Text>
  </View>
);

const statStyles = StyleSheet.create({
  statGroup: {
    flexDirection: 'row',
    alignItems:    'center',
  },
  sep: {
    fontSize:          FontSize.xs - 1,
    opacity:           0.35,
    paddingHorizontal: Spacing.xs,
  },
  text: {
    fontSize:   FontSize.xs - 1,
    fontWeight: FontWeight.medium,
    fontVariant: ['tabular-nums'],
  },
});

// ─── Props ────────────────────────────────────────────────────────────────────

interface TransferCardProps {
  session:   TransferSession;
  index?:    number;
  onPause:   (id: string) => void;
  onResume:  (id: string) => void;
  onCancel:  (id: string, title?: string) => void;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const TransferCard: React.FC<TransferCardProps> = ({
  session,
  index = 0,
  onPause,
  onResume,
  onCancel,
  selected,
  onToggleSelect,
}) => {
  const { colors, isDark } = useTheme();
  const [copied, setCopied] = useState(false);

  const isActive     = session.status === 'transferring' || session.status === 'connecting' || session.status === 'seeding';
  const isRecovering = session.status === 'recovering';
  const pct          = Math.round((session.progress ?? 0) * 100);

  // Recovering sessions use an amber accent to signal they are reconnecting,
  // not paused by the user and not failed.
  const progressColor = isRecovering ? colors.warning : colors.brand;

  const statusLabel =
    session.status === 'transferring'
      ? (session.direction === 'receive' ? 'Downloading' : 'Uploading')
      : session.status === 'recovering'
        ? 'Reconnecting…'
        : session.status.charAt(0).toUpperCase() + session.status.slice(1);

  const fileLabel = !session.files?.length
    ? 'Connecting…'
    : session.files.length === 1
      ? session.files[0].name
      : `${session.files[0].name} (+${session.files.length - 1})`;

  const copyMagnet = () => {
    if (!session.magnetUri) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(session.magnetUri);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      Alert.alert('Magnet Link', session.magnetUri);
    }
  };

  return (
    <View style={[styles.row, { backgroundColor: selected ? colors.pill : colors.surface, borderColor: colors.border }]}>
      {/* Checkbox */}
      <TouchableOpacity 
        style={[styles.checkbox, { borderColor: selected ? colors.brand : colors.subtext, backgroundColor: selected ? colors.brand : 'transparent' }]}
        onPress={() => onToggleSelect?.(session.id)}
        activeOpacity={0.8}
      >
        {selected && <Icon name="check" size={12} color={colors.surface as string} />}
      </TouchableOpacity>

      {/* Left status / control icon */}
      <TouchableOpacity
        style={[styles.statusIconBadge, { backgroundColor: colors.pill }]}
        onPress={() => {
          if (session.status === 'completed' || session.status === 'failed' || isRecovering) return;
          isActive ? onPause(session.id) : onResume(session.id);
        }}
        activeOpacity={0.8}
        disabled={session.status === 'completed' || session.status === 'failed' || isRecovering}
      >
        <Icon
          name={
            session.status === 'completed' ? 'check'
            : isRecovering               ? 'refresh-cw'
            : isActive                   ? 'pause'
            :                              'play'
          }
          size={15}
          color={
            session.status === 'completed' ? colors.brand as string
            : isRecovering               ? colors.warning as string
            :                              colors.text as string
          }
        />
      </TouchableOpacity>

      {/* File info + progress */}
      <View style={styles.centerCol}>
        <Text style={[styles.filename, { color: colors.text }]} numberOfLines={1}>{fileLabel}</Text>

        {/* Stats row */}
        <View style={styles.statsRow}>
          <Text style={[styles.statusLabel, { color: isRecovering ? colors.warning : isActive ? colors.brand : colors.subtext }]}>
            {statusLabel}
          </Text>
          <Stat label={fmtSpeed(session.speed)}      color={colors.subtext} />
          <Stat label={fmtSpeed(session.uploadSpeed ?? 0)} color={colors.subtext} />
          <Stat label={`${session.peersCount ?? 0} peers`} color={colors.subtext} />
          <Stat label={`${fmtBytes(session.bytesTransferred)} / ${fmtBytes(session.totalBytes)}`} color={colors.subtext} />
          <Stat label={fmtEta(session.eta)}           color={colors.subtext} />
          <Text style={[styles.pct, { color: colors.subtext }]}>  {pct}%</Text>
        </View>

        <ProgressBar progress={session.progress ?? 0} color={progressColor} height={3} />
      </View>

      {/* Actions */}
      <View style={styles.rightCol}>
        <TouchableOpacity onPress={copyMagnet} style={styles.shareBtn} activeOpacity={0.75}>
          <Icon name="copy" size={15} color={colors.subtext} />
          <Text style={[styles.shareLbl, { color: colors.subtext }]}>{copied ? 'Copied!' : 'Share'}</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.iconBtn} onPress={() => onCancel(session.id, fileLabel)} activeOpacity={0.75}>
          <Icon name="trash" size={15} color={colors.subtext} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  row: {
    flexDirection:   'row',
    alignItems:      'center',
    borderWidth:     1,
    borderRadius:    Radius.lg,
    marginBottom:    Spacing.sm,
    paddingHorizontal: 14,
    paddingVertical: 12,
    minHeight:       72,
  },
  checkbox: {
    width: 16, height: 16,
    borderWidth: 1.5,
    borderRadius: Radius.sm,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: Spacing.md,
  },
  statusIconBadge: {
    width:          34,
    height:         34,
    borderRadius:   Radius.full,
    justifyContent: 'center',
    alignItems:     'center',
    marginRight:    Spacing.md,
  },
  centerCol: { flex: 1, marginRight: 12 },
  filename: {
    fontSize:   FontSize.md,
    fontWeight: FontWeight.semibold,
    marginBottom: 4,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems:    'center',
    marginBottom:  6,
    flexWrap:      'nowrap',
  },
  statusLabel: {
    fontSize:   FontSize.xs,
    fontWeight: FontWeight.bold,
    fontVariant: ['tabular-nums'],
  },
  pct: {
    fontSize:   FontSize.xs,
    fontWeight: FontWeight.semibold,
    marginLeft: 4,
    fontVariant: ['tabular-nums'],
  },
  rightCol: {
    flexDirection: 'row',
    alignItems:    'center',
    gap: 2,
  } as any,
  shareBtn: {
    flexDirection: 'row',
    alignItems:    'center',
    paddingHorizontal: Spacing.sm,
    paddingVertical:   Spacing.xs,
    borderRadius:  Radius.md,
    marginRight:   Spacing.xs,
  },
  shareLbl: {
    fontSize:   FontSize.xs,
    fontWeight: FontWeight.semibold,
    marginLeft: 4,
  },
  iconBtn: {
    width: 28, height: 28,
    borderRadius: Radius.md,
    justifyContent: 'center',
    alignItems:     'center',
  },
});
